import Editor from './editor'
import ListFilter from '@components/list-filter'
import {useAssetsSettings, useTenant} from '@store/settings'
import {Button, Card, Space, Table} from 'antd'
import PageHeader from '@components/page-header'
import {Params, useFetchGet} from 'helpers/api'
import {isSysAdmin} from 'helpers/role'
import {paginationDefaults, widthLarge} from 'helpers/style'
import _ from 'lodash'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import GridRow from '@components/grid-row'
import {formatDateTime} from 'helpers/dates'
import {useDomain} from 'contexts/DomainContext'

const getColumns = (tenant, handlers, terms) =>
  [
    {
      title: terms.tenant,
      render: (_, record) => (
        <Button
          type="link"
          onClick={e => {
            e.stopPropagation()
            handlers.tenant(record.tenant)
          }}
        >
          {record.tenant.name}
        </Button>
      ),
      hidden: !!tenant
    },
    {
      title: terms.site,
      render: (_, record) => record.site.name
    },
    {
      title: terms.gateway,
      render: (_, record) => (
        <Button
          type="link"
          onClick={e => {
            e.stopPropagation()
            handlers.gateway(record.tenant, record.gateway)
          }}
        >
          {record.gateway.name}
        </Button>
      )
    },
    {
      title: terms.camera,
      render: (_, record) => record.camera.name
    },
    {
      title: terms.target,
      render: (_, record) => (
        <Button
          type="link"
          onClick={e => {
            e.stopPropagation()
            handlers.target(record.tenant, record.target)
          }}
        >
          {record.target.name}
        </Button>
      )
    },
    {
      title: 'Last Data Received',
      render: (_, record) => {
        let timestamp = null
        if (record.target && record.target.timestamp) {
          timestamp = record.target.timestamp
        } else if (record.camera && record.camera.timestamp) {
          timestamp = record.camera.timestamp
        } else if (record.gateway && record.gateway.timestamp) {
          timestamp = record.gateway.timestamp
        }

        if (timestamp) {
          // const minutesSinceLastTimestamp = moment().diff(timestamp, 'minutes')
          // const status = minutesSinceLastTimestamp <= record.site.grace_period ? 'good' : 'bad'
          return formatDateTime(timestamp)
        } else {
          return <></>
        }
      }
    },
    {
      title: (
        <Button
          data-testid="add"
          className="add"
          type="link"
          onClick={() => {
            handlers.item({})
          }}
        >
          Add
        </Button>
      ),
      key: 'action',
      align: 'right',
      width: 200,
      render: (_, record) => {
        return (
          <Space size="middle">
            <Button
              type="link"
              onClick={e => {
                e.stopPropagation()
                handlers.item(record)
              }}
            >
              Edit/Add
            </Button>
          </Space>
        )
      }
    }
  ].filter(item => !item.hidden)

const Assets = () => {
  const navigate = useNavigate()
  const [filter, setFilter, page, setPage, limit, setLimit] = useAssetsSettings()
  const [selectedItem, setSelectedItem] = useState()
  const user = useSelector(state => state.auth.user)
  const [search, setSearch] = useState(null)
  const [searchMeta, setSearchMeta] = useState(null)
  const fetchSearch = useFetchGet('search')
  const [tenant, setTenant] = useTenant()
  const {terms} = useDomain()

  const handleTenantClick = useCallback(
    tenant => {
      if (!tenant?.id) {
        console.error('Invalid tenant object:', tenant)
        return
      }
      setTenant(tenant)
    },
    [setTenant] // setTenant is a dependency
  )

  const getSearch = () => {
    const params = Params({searchTerm: filter, limit, page})
    if (isSysAdmin(user) && tenant) params.append('tid', tenant.id)
    fetchSearch(params, response => {
      setSearch(response.search)
      setSearchMeta(response.meta)
    })
  }

  useEffect(() => {
    getSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, page, limit, user, tenant])

  const data = useMemo(() => {
    return tenant && search && !search[0]?.site?.id ? search.slice(1) : search
  }, [search, tenant])

  const handleGatewayClick = (gateway_tenant, gateway) => {
    if (gateway_tenant?.id !== tenant?.id) {
      handleTenantClick(gateway_tenant)
    }
    navigate(`/gateway/${gateway?.id}`)
  }

  const handleTargetClick = (target_tenant, target) => {
    if (target_tenant?.id !== tenant?.id) {
      handleTenantClick(target_tenant)
    }
    navigate(`/target/${target?.id}`)
  }

  const columns = useMemo(() => {
    const getAllCols = getColumns(
      tenant,
      {
        tenant: handleTenantClick,
        gateway: handleGatewayClick,
        target: handleTargetClick,
        item: setSelectedItem
      },
      terms
    )
    if (user?.role !== 'sysadmin') {
      return _.remove(getAllCols, col => {
        return user?.role === 'sysadminRO' || col.key !== 'action'
      })
    }
    return getAllCols
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, tenant, terms])

  const rowKey = record => {
    return `${record?.tenant.id},${record?.site?.id},${record?.gateway?.id},${record?.camera?.id},${record?.target?.id}`
  }

  return (
    <>
      <GridRow layout={[24]}>
        <Card className={`w-${widthLarge}`}>
          <PageHeader
            ghost={false}
            onBack={() => window.history.back()}
            title="Assets"
            subTitle=""
            backIcon={false}
            extra={[
              <ListFilter
                onFilter={value => {
                  setFilter(value)
                  setPage(1)
                }}
                filter={filter}
                tenant={tenant}
              />
            ]}
          />
          <Table
            data-testid="assets-table"
            columns={columns}
            dataSource={data}
            loading={!data}
            pagination={paginationDefaults(searchMeta?.total, limit, setLimit, searchMeta?.page, setPage)}
            rowKey={record => rowKey(record)}
            rowClassName="antd-table-row"
          />
        </Card>
      </GridRow>
      {typeof selectedItem === 'object' && (
        <Editor item={selectedItem} onClose={() => setSelectedItem()} onSuccess={() => getSearch()} />
      )}
    </>
  )
}

export default Assets
